if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        if (!this.form) return;

        this.formInput = this.form.querySelector('[name="id"]');
        if (this.formInput) this.formInput.disabled = false;

        this.cart = document.querySelector("cart-drawer");
        this.cartType = this.cart?.dataset?.cartType || null;

        this.submitButton = this.querySelector('[type="submit"]');
        this.body = document.body;
        this.hideErrors = this.dataset.hideErrors === "true";
        this.isSubmitting = false;
        this.error = false;
        this.isThemeEditor = !!(window.Shopify && Shopify.designMode);

        if (this.cart && this.submitButton) {
          this.submitButton.setAttribute("aria-haspopup", "dialog");
        }

        this.handleErrorMessage = this.handleErrorMessage.bind(this);
        this.redirectAfterSubmit = this.redirectAfterSubmit.bind(this);
        this.disableLoadingInButton = this.disableLoadingInButton.bind(this);
        this.onSubmitHandler = this.onSubmitHandler.bind(this);

        this.form.addEventListener("submit", this.onSubmitHandler);
      }

      safe(fn) {
        try {
          return fn();
        } catch (e) {
          return null;
        }
      }

      safePublish(eventName, payload) {
        if (
          typeof publish === "function" &&
          typeof PUB_SUB_EVENTS !== "undefined" &&
          PUB_SUB_EVENTS[eventName]
        ) {
          publish(PUB_SUB_EVENTS[eventName], payload);
        }
      }

      getSectionsToRequest() {
        if (
          this.cart &&
          typeof this.cart.getSectionsToRender === "function"
        ) {
          const sections = this.cart.getSectionsToRender() || [];
          return sections
            .map((section) => section.id)
            .filter(Boolean);
        }

        return [];
      }

      setLoadingState(isLoading) {
        if (!this.submitButton) return;

        const loader = this.querySelector(".loading-overlay__spinner");

        if (isLoading) {
          this.submitButton.setAttribute("aria-disabled", "true");
          this.submitButton.classList.add("loading");
          if (loader) loader.classList.remove("hidden");
        } else {
          this.submitButton.classList.remove("loading");
          if (loader) loader.classList.add("hidden");
          this.submitButton.removeAttribute("aria-disabled");
        }
      }

      closeComplementaryProduct() {
        const complementaryOverlayPage = document.querySelector(
          ".wt__quick-buy--page-overlay"
        );

        if (this.body.classList.contains("quick-buy-page-overlay")) {
          this.body.classList.remove("quick-buy-page-overlay");
        }

        if (
          complementaryOverlayPage &&
          complementaryOverlayPage.classList.contains(
            "wt__quick-buy--page-overlay--open"
          )
        ) {
          complementaryOverlayPage.classList.remove(
            "wt__quick-buy--page-overlay--open"
          );
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (!this.form || !this.submitButton) return;
        if (this.isSubmitting) return;
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.isSubmitting = true;
        this.error = false;

        this.handleErrorMessage();
        this.safe(() => {
          if (this.cart && typeof this.cart.setActiveElement === "function") {
            this.cart.setActiveElement(document.activeElement);
          }
        });

        this.setLoadingState(true);

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        const sections = this.getSectionsToRequest();

        if (sections.length) {
          formData.append("sections", sections.join(","));
          formData.append("sections_url", window.location.pathname);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.error = true;

              this.safePublish("cartError", {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message
              });

              this.handleErrorMessage(response.description || response.message || "There was an error adding this item to the cart.");

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");

              if (soldOutMessage) {
                const mainText = this.submitButton.querySelector("span");
                this.submitButton.setAttribute("aria-disabled", "true");
                if (mainText) mainText.classList.add("hidden");
                soldOutMessage.classList.remove("hidden");
              }

              return;
            }

            if (!this.cart || this.cartType !== "drawer") {
              window.location = window.routes.cart_url;
              return;
            }

            this.safePublish("cartUpdate", {
              source: "product-form",
              productVariantId: formData.get("id"),
              cartData: response
            });

            const quickAddModal = this.closest("quick-add-modal");

            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.redirectAfterSubmit(response);
                  }, 0);
                },
                { once: true }
              );

              if (this.cartType === "drawer") {
                this.safe(() => quickAddModal.hide(true));
              }
            } else {
              const isClosedCart = !document.body.classList.contains(
                "page-overlay-cart-on"
              );
              this.redirectAfterSubmit(response, isClosedCart);
            }
          })
          .catch((e) => {
            console.error("Product form add-to-cart error:", e);
            this.error = true;
            this.handleErrorMessage("Unable to add this item right now. Please try again.");
          })
          .finally(() => {
            this.isSubmitting = false;
            this.disableLoadingInButton();

            if (this.cart && this.cart.classList.contains("is-empty")) {
              this.cart.classList.remove("is-empty");
            }

            this.safe(() => {
              const quickAddContainer = document.querySelector(
                ".wt__quick-buy__container"
              );
              const quickAddOverlay = document.querySelector(
                ".wt__quick-buy--page-overlay"
              );

              if (!quickAddContainer) return;

              const quickAddProduct =
                quickAddContainer.querySelector(".wt-product");
              const loader =
                quickAddContainer.querySelector(".wt__quick-buy-loader");

              if (!quickAddContainer.classList.contains("hidden")) {
                if (quickAddProduct) quickAddProduct.remove();
                quickAddContainer.classList.remove(
                  "wt__quick-buy__container--open"
                );

                if (quickAddOverlay) {
                  quickAddOverlay.classList.remove(
                    "wt__quick-buy--page-overlay--open"
                  );
                }

                document.body.classList.remove("quick-buy-page-overlay");

                if (loader) loader.classList.remove("hidden");
              }
            });
          });
      }

      disableLoadingInButton() {
        this.setLoadingState(false);
      }

      redirectAfterSubmit(response, isClosedCart = true) {
        const isCartPage = document.body.classList.contains("template-cart");

        if (this.cartType === "drawer" && isCartPage) {
          const overlay = document.querySelector(".wt__quick-buy--page-overlay");
          if (overlay) {
            overlay.classList.remove("wt__quick-buy--page-overlay--open");
          }
          document.body.classList.remove("quick-buy-page-overlay");
          return;
        }

        if (
          this.cartType === "drawer" &&
          this.cart &&
          typeof this.cart.renderContents === "function"
        ) {
          const rendered = this.safe(() => {
            this.cart.renderContents(response, isClosedCart);
            return true;
          });

          if (rendered) return;
        }

        window.location = window.routes.cart_url;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}